/**
 * The always-on inner-power aura (ไอกำลังภายใน, DECISIONS 2026-10-13): round everyone who trains a neigong, in its
 * colour, growing with its level (looks and level source: render/innerAuraLooks.ts; setting `innerAura`).
 *
 * Drawn on the ground layer, under the scenery and every figure: it reads behind and round the body and never covers a
 * figure, a wind-up tell or a ground telegraph (those draw later; telegraphs are order 220). Cheap: each aura is one
 * cached frame (`drawImage` of a crop of a strip painted once), off-screen ones are skipped and the count drawn is
 * capped by the graphics tier, nearest the player first. In a bout a fighter winding up drops to the faint band so
 * the tell stays the loudest thing on them.
 */
import type { Entity } from '@nine/shared';
import '../innerAura.setting';
import { settings } from '../../settings';
import { tierIndex } from '../../graphics';
import { visualScale } from '../sprites';
import { duelRing } from './duelRing';
import { registerRenderPass } from './registry';
import { auraFrameBytes, auraFrames, BAND_FPS, bandOf, innerPowerOf, NF, tintOf, type AuraBand } from '../innerAuraLooks';

/** The most auras drawn at once, by graphics tier (0 lowest). */
const CAP = [4, 6, 10, 14, 20] as const;
/** The visible figure's height as a share of its sprite set's height (the set keeps headroom over the hair). */
const FIT = 0.86;

/** Diagnostics (dev): auras drawn last frame and the pass's time. */
export const innerAuraStats = { drawn: 0, ms: 0, bytes: 0 };
if (import.meta.env.DEV) Object.assign(window, { __innerAura: innerAuraStats });

interface Pick {
  e: Entity;
  d: number;
}

registerRenderPass<{ picks: Pick[] }>({
  id: 'innerAura',
  layer: 'ground',
  order: 212,
  state: () => ({ picks: [] }),
  draw(f, s) {
    const mode = settings.innerAura;
    innerAuraStats.drawn = 0;
    if (mode === 2) return;
    const t0 = performance.now();
    const { world: w, iso, g, ox, oy, visible: v, effects } = f;
    const me = w.player;
    const picks = s.picks;
    picks.length = 0;
    if (mode === 1) {
      if (!me.dead) picks.push({ e: me, d: 0 });
    } else
      for (const e of f.shown()) {
        if (e.dead || (e.kind !== 'player' && e.kind !== 'rival' && e.kind !== 'npc')) continue;
        if (e.folk?.indoors) continue;
        picks.push({ e, d: e === me ? -1 : Math.hypot(e.x - me.x, e.y - me.y) });
      }
    if (picks.length > 1) picks.sort((a, b) => a.d - b.d);
    const cap = CAP[tierIndex()];
    const bout = duelRing.on || !!w.pvpBout;
    const k = iso.figureK;
    let drawn = 0;
    for (const { e } of picks) {
      if (drawn >= cap) break;
      const ip = innerPowerOf(w, e);
      // A mastered neigong carries its own mastery aura (render/auras.ts).
      if (!ip || ip.level >= ip.max) continue;
      const set = f.sprites.of(e);
      if (!set) continue;
      const figH = Math.max(24, Math.round((set.h * visualScale(e, set) * k * FIT) / 2) * 2);
      const [sx, sy] = iso.proj(e.x, e.y);
      const x = Math.round(sx + ox);
      const ground = Math.round(sy + oy - effects.stepElevation(e));
      const y = ground - Math.round(e.z) - Math.round(effects.hoverOf(e));
      if (x < v.x - 40 || x > v.x + v.w + 40 || y < v.y - 20 || y - figH > v.y + v.h + 20) continue;
      let band: AuraBand = bandOf(ip.level, ip.max);
      if (bout && e.cast) band = 0;
      const fr = auraFrames(tintOf(ip.neigong), band, figH);
      const fi = (Math.floor(w.time * BAND_FPS[band]) + e.id * 5) % NF;
      if (fr.ground && e.z < 3)
        g.drawImage(fr.ground, fi * fr.gw, 0, fr.gw, fr.gh, x - fr.gx, ground - fr.gy, fr.gw, fr.gh);
      g.drawImage(fr.body, fi * fr.w, 0, fr.w, fr.h, x - fr.ax, y - fr.ay, fr.w, fr.h);
      drawn++;
    }
    innerAuraStats.drawn = drawn;
    innerAuraStats.ms = performance.now() - t0;
    if (import.meta.env.DEV) innerAuraStats.bytes = auraFrameBytes();
  },
});
